import { atom, AtomEffect } from "recoil";

type BoardMissionModalStep = "stations" | "files" | "activity";
type BoardMissionModalView = "task" | "orderSummary";

interface BoardMissionModalState {
  isOpen: boolean;
  boardMissionsId: string | null;
  productType: string;
  step: BoardMissionModalStep;
  /** Parent order context. Required by the order summary view, optional for the task view. */
  orderId?: string | null;
  /** Defaults to the task view when absent, so existing call sites keep their behavior. */
  view?: BoardMissionModalView;
}

const ORDER_SUMMARY_VIEW_QUERY_VALUE = "order-summary";

const STORAGE_KEY = "production-floor.board-mission-modal-state";

const sessionStorageEffect =
  <T,>(key: string, hydrate: (stored: T) => T = (stored) => stored): AtomEffect<T> =>
  ({ setSelf, onSet, trigger }) => {
    if (typeof window === "undefined") {
      return;
    }

    if (trigger === "get") {
      const storedValue = window.sessionStorage.getItem(key);
      if (storedValue) {
        try {
          setSelf(hydrate(JSON.parse(storedValue)));
        } catch {
          window.sessionStorage.removeItem(key);
        }
      }
    }

    onSet((newValue, _, isReset) => {
      if (isReset) {
        window.sessionStorage.removeItem(key);
        return;
      }
      window.sessionStorage.setItem(key, JSON.stringify(newValue));
    });
  };

export const defaultBoardMissionModalState: BoardMissionModalState = {
  isOpen: false,
  boardMissionsId: null,
  productType: "",
  step: "stations",
  orderId: null,
  view: "task",
};

export const boardMissionModalState = atom<BoardMissionModalState>({
  key: "boardMissionModalState",
  default: defaultBoardMissionModalState,
  effects_UNSTABLE: [
    // State written by an older tab can be missing the newer fields, so merge over the defaults.
    sessionStorageEffect<BoardMissionModalState>(STORAGE_KEY, (stored) => ({
      ...defaultBoardMissionModalState,
      ...stored,
    })),
  ],
});

/** Bumped after mutations that Order Summary should refetch for (e.g. mark unready). */
export const orderSummaryRefreshKeyState = atom<number>({
  key: "orderSummaryRefreshKeyState",
  default: 0,
});

export { ORDER_SUMMARY_VIEW_QUERY_VALUE };
export type {
  BoardMissionModalState,
  BoardMissionModalStep,
  BoardMissionModalView,
};
